/**
 * WellnessTab — Careflow plan display for the device.
 *
 * Filters careflow windows by:
 *   - escalation_type_id 3 (device-specific, matching deviceId)
 *   - escalation_type_id 4 (resident group, matching device's resGroups)
 *   - escalation_type_id 5 (area group, matching device's areaGroups)
 *   - escalation_type_id 1 (default / campus-wide)
 *
 * Display order: Device → Resident → Area → Default
 */

import { useMemo } from 'react';
import { convertSectoHHMM, HHMMtoLocalTime } from '../../../../libs.jsx';

const WellnessTab = ({ careData, deviceId, areaGroups, resGroups }) => {
  const careflowItems = useMemo(() => {
    if (!careData?.length) return [];

    const deviceFlows = [];
    const residentFlows = [];
    const areaFlows = [];
    const defaultFlows = [];

    for (const cf of careData) {
      const item = {
        name: cf.careflow_description,
        window: `${HHMMtoLocalTime(convertSectoHHMM(cf.enable_start))} - ${HHMMtoLocalTime(convertSectoHHMM(cf.enable_end))}`,
      };

      if (cf.escalation_type_id === 1) {
        defaultFlows.push({ ...item, type: 'Default' });
      } else if (cf.escalation_type_id === 3) {
        // Device-specific: check if deviceId is in references
        if (cf.references?.split(',').includes(deviceId.toString())) {
          deviceFlows.push({ ...item, type: 'Device' });
        }
      } else if (cf.escalation_type_id === 4) {
        // Resident group: check if any of device's resident groups match
        for (const rg of (resGroups || [])) {
          if (cf.references?.split(',').includes(rg.resident_group_id?.toString())) {
            residentFlows.push({ ...item, type: 'Resident' });
            break;
          }
        }
      } else if (cf.escalation_type_id === 5) {
        // Area group: check if any of device's area groups match
        for (const ag of (areaGroups || [])) {
          if (cf.references?.split(',').includes(ag.area_group_id?.toString())) {
            areaFlows.push({ ...item, type: 'Area' });
            break;
          }
        }
      }
    }

    return [...deviceFlows, ...residentFlows, ...areaFlows, ...defaultFlows];
  }, [careData, deviceId, areaGroups, resGroups]);

  return (
    <div className="pp-section">
      <div className="pp-section__header">
        <h3 className="pp-section__title">Careflow Plan</h3>
      </div>
      <div className="pp-section__body">
        {careflowItems.length > 0 ? (
          <div>
            {careflowItems.map((item, idx) => (
              <div key={idx} className="ip-careflow-item">
                <span className="ip-careflow-item__name" title={item.name}>
                  {item.name}
                </span>
                <span className="ip-careflow-item__window">{item.window}</span>
                <span className="ip-careflow-item__type">{item.type}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="dp-empty">
            <p className="dp-empty__title">No Careflow</p>
            <p className="dp-empty__text">No careflow plans are currently assigned to this device.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WellnessTab;
